import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/config";
import { getLocalizedTitle, getLocalizedDescription, getLocalizedField, isValidValue } from "../utils/localization";
import Navbar from "../component/navbar";
import Hero from "../component/home/Hero";
import PartnerLogos from "../component/home/PartnerLogos";
import {
  apiBrandStory,
  apiSupremeConcept,
  apiNewsPromotion,
  apiListBanners,
  apiProjectLogos,
  apiLivingStandards,
  apiOurCollection,
  apiPromotion,
} from "../services/apiClient";

// Lazy load components
const BrandStory = lazy(() => import("../component/home/BrandStory"));
const SupremeConcept = lazy(() => import("../component/home/SupremeConcept"));
const LivingStandards = lazy(() => import("../component/home/LivingStandards"));
const NewsPromotion = lazy(() => import("../component/home/NewsPromotion"));
const OurCollection = lazy(() => import("../component/home/OurCollection"));
const Footer = lazy(() => import("../component/home/Footer"));

export default function LandingModule() {
  const location = useLocation();
  const [heroData, setHeroData] = useState(null);
  const [partnerLogos, setPartnerLogos] = useState([]);
  const [brandStory, setBrandStory] = useState(null);
  const [supremeConcept, setSupremeConcept] = useState(null);
  const [newsPromotion, setNewsPromotion] = useState(null);
  const [livingStandardCategories, setLivingStandardCategories] = useState([]);
  const [ourCollection, setOurCollection] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const NAV_OFFSET = 72;

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // Fetch logos first (optional)
        const logosRes = await apiProjectLogos();
        if (mounted) setPartnerLogos(mapPartnerLogos(logosRes?.data ?? []));

        const [
          bannersRes,
          promotionRes,
          brandStoryRes,
          conceptRes,
          newsRes,
          livingRes,
          ourCollectionRes,
        ] = await Promise.all([
          apiListBanners(),
          apiPromotion(),
          apiBrandStory(),
          apiSupremeConcept(),
          apiNewsPromotion(),
          apiLivingStandards(),
          apiOurCollection(),
        ]);

        if (mounted) {
          setHeroData(
            mapHeroData(
              bannersRes?.data ?? [],
              promotionRes?.data ?? []
            )
          );

          setBrandStory(mapBrandStory(brandStoryRes?.data ?? brandStoryRes));
          setSupremeConcept(mapConcepts(conceptRes?.data ?? []));
          setNewsPromotion(mapNews(newsRes?.data ?? []));
          setLivingStandardCategories(mapLivingCategories(livingRes?.data ?? []));
          setOurCollection(mapProjects(ourCollectionRes?.data ?? []));
        }
      } catch (err) {
        console.error("Landing data error", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [i18n.language]); // Re-fetch when language changes

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  // เลื่อนไป section เมื่อ URL มี hash #living (เช่น จาก Footer ลิงก์ /#living)
  useEffect(() => {
    if (location.hash === "#living") {
      const el = document.getElementById("living");
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
  }, [location.hash]);

  // ฟังก์ชัน refetch Living Standards categories
  const refetchLivingStandards = useMemo(() => {
    return async () => {
      try {
        const livingRes = await apiLivingStandards();
        setLivingStandardCategories(mapLivingCategories(livingRes?.data ?? []));
      } catch (err) {
        console.error("LivingStandards refetch error:", err);
      }
    };
  }, []);

  const onNav = useMemo(() => {
    return (key) => {
      switch (key) {
        case "home":
          return scrollToId("hero");
        case "brand-story":
          return scrollToId("brand-story");
        case "concept":
          return scrollToId("concept");
        case "living":
          return scrollToId("living");
        case "news":
          return scrollToId("news");
        case "projects":
          return scrollToId("projects");
        case "contact":
          return scrollToId("contact");
        case "career":
          return scrollToId("career");
        default:
          return;
      }
    };
  }, []);

  return (
    <div className="font-sans text-gray-800 w-full overflow-x-hidden min-w-0">
      <Navbar onNav={onNav} />

      {/* Add padding-top to account for fixed navbar */}
      <div className="pt-12 sm:pt-14 md:pt-16">
        <section id="hero">
          <Hero data={heroData} onNav={onNav} loading={isLoading} />
        </section>

      <section id="brand-story">
        <Suspense fallback={<div className="h-64 bg-white animate-pulse" />}>
          <BrandStory data={brandStory} loading={isLoading} />
        </Suspense>
      </section>

      <section id="concept">
        <Suspense fallback={<div className="h-96 bg-[#F8F6F4] animate-pulse" />}>
          <SupremeConcept data={supremeConcept} loading={isLoading} />
        </Suspense>
      </section>

      <section id="living">
        <Suspense fallback={<div className="h-96 bg-white animate-pulse" />}>
          <LivingStandards 
            categories={livingStandardCategories}
            onTick={refetchLivingStandards}
            loading={isLoading}
          />
        </Suspense>
      </section>

      <section id="news">
        <Suspense fallback={<div className="h-96 bg-[#F8F6F4] animate-pulse" />}>
          <NewsPromotion data={newsPromotion} loading={isLoading} />
        </Suspense>
      </section>

      <section id="projects">
        <Suspense fallback={<div className="h-96 bg-white animate-pulse" />}>
          <OurCollection projects={ourCollection} loading={isLoading} />
        </Suspense>
      </section>

      <section id="logos" className="py-8">
        <PartnerLogos items={partnerLogos} loading={isLoading} />
      </section>

      <Suspense fallback={<div className="h-64 bg-[#2f4b44] animate-pulse" />}>
        <Footer loading={isLoading} />
      </Suspense>
      </div>
    </div>
  );
}

/* ----------------- mappers ----------------- */

function pickLatestPromotion(items = []) {
  if (!Array.isArray(items) || items.length === 0) return null;

  const toTime = (x) => {
    const t = Date.parse(x || "");
    return Number.isFinite(t) ? t : 0;
  };

  return [...items].sort((a, b) => {
    const at = toTime(a.publish_date) || toTime(a.created_at);
    const bt = toTime(b.publish_date) || toTime(b.created_at);
    return bt - at;
  })[0];
}

function mapHeroData(banners = [], promotions = []) {
  const slides = (banners ?? []).map((b) => ({
    id: b.uuid ?? b.banner_url,
    bannerUrl: b.banner_url,
    title1: b.title ?? i18n.t("landing.heroTitle1"),
    title2: "",
    subtitle: b.description ?? "",
    category: b.category,
  }));

  if (slides.length === 0) {
    slides.push({
      id: "fallback",
      bannerUrl: "/assets/hero.jpg",
      title1: i18n.t("landing.heroTitle1"),
      title2: i18n.t("landing.heroTitle2"),
      subtitle: i18n.t("landing.heroSubtitle"),
    });
  }

  const latestPromo = pickLatestPromotion(promotions);
  const promotionBox = latestPromo ? mapPromotionToHeroBox(latestPromo) : null;

  return {
    slides,
    cta: { label: i18n.t("landing.liveAndInvest") },
    stats: { projects: "—", years: "—", note: "" },
    promotionBox,
  };
}

function stripMarkdown(s = "") {
  return String(s)
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\r/g, "");
}

function extractPromotionParts(text = "") {
  const s = String(text).trim();

  const key = "ข้อเสนอพิเศษ";
  const idx = s.indexOf(key);

  let intro = s;
  let tail = "";

  if (idx >= 0) {
    intro = s.slice(0, idx).trim();
    tail = s.slice(idx).trim();
  } else {
    intro = s.split("\n").slice(0, 3).join("\n").trim();
  }

  const stopWords = ["โครงการที่ร่วมรายการ", "ระยะเวลาโปรโมชั่น", "สอบถามรายละเอียดเพิ่มเติม"];
  for (const w of stopWords) {
    const p = tail.indexOf(w);
    if (p >= 0) tail = tail.slice(0, p);
  }

  const benefits = [];
  const dashParts = tail.split(/\s-\s/g).map((x) => x.trim()).filter(Boolean);

  for (const part of dashParts) {
    const t = part.replace(/^ข้อเสนอพิเศษ:?\s*/g, "").trim();
    if (t) benefits.push(t);
  }

  return {
    intro,
    benefits: benefits.slice(0, 6),
  };
}

function mapPromotionToHeroBox(promo) {
  const raw = stripMarkdown(promo.description ?? "");
  const { intro, benefits } = extractPromotionParts(raw);

  return {
    title: promo.title ?? i18n.t("landing.promotion"),
    description: intro || i18n.t("landing.discoverPromotion"),
    benefits,
    imageUrl: promo.image_url,
    slug: promo.slug,
  };
}



function mapPartnerLogos(items = []) {
  return items.map((it) => ({
    id: it.uuid,
    name: it.name,
    logoUrl: it.logo_url,
  }));
}

function mapBrandStory(data) {
  if (!data) return null;

  // ใช้ภาษาปัจจุบัน (th/en) เหมือน SupremeConcept ผ่าน getLocalizedField
  const description =
    getLocalizedField(data, "overview", "") ||
    getLocalizedField(data, "story", "") ||
    "";

  return {
    title: i18n.t("landing.brandStory"),
    companyName: i18n.t("landing.supremeTeam"),
    description: description.trim() || "",
    readMoreLabel: i18n.t("landing.readOurStory"),
  };
}

function mapConcepts(items = []) {
  if (!items.length) return null;

  const getIconUrl = (iconName) =>
    `/assets/icons/${String(iconName ?? "").toLowerCase().trim()}.svg`;

  return {
    title: i18n.t("landing.supremeConcept"),
    concepts: items.map((c, idx) => {
      // Use localized fields with fallback to English if Thai not available
      const title = getLocalizedTitle(c, "");
      const description = getLocalizedDescription(c, "");

      return {
        id: c.uuid ?? idx,
        title,
        description,
        icon: getIconUrl(c.icon_name),
      };
    }),
  };
}

function mapNews(items = []) {
  if (!items.length) return null;

  const news = items.map((n) => {
    const dateStr = n.publish_date ?? n.created_at ?? n.create_at ?? null;

    const d = dateStr ? new Date(dateStr) : null;
    const day = d ? String(d.getUTCDate()).padStart(2, "0") : "—";
    const month = d
      ? d.toLocaleString("en-US", { month: "short", timeZone: "UTC" })
      : "";

    return {
      id: n.uuid ?? n.slug ?? n.title,
      title: n.title,
      description: n.description,
      imageUrl: n.image_url,
      day,
      month,
    };
  });

  return { title: i18n.t("landing.whatsUp"), learnMoreLabel: i18n.t("landing.learnMore"), news };
}


function mapLivingCategories(data = []) {
  // Data structure: [{ category: "design", items: [...] }, { category: "sustainability", items: [...] }]
  if (!Array.isArray(data)) return [];
  
  return data.map((categoryData) => ({
    category: categoryData.category || "default",
    items: (categoryData.items || []).map((item) => ({
      uuid: item.uuid,
      title: item.title || "-",
      image_url: item.image_url,
      sequence: item.sequence ?? 0,
      category: item.category || categoryData.category,
      created_at: item.created_at,
    })),
  })).filter((cat) => cat.items && cat.items.length > 0);
}

function mapProjects(items = []) {
  return items.map((p) => ({
    id: p.uuid ?? p.name,
    uuid: p.uuid,
    name: p.name,
    location: p.location,
    price: p.price,
    price_start: p.price_start,
    price_end: p.price_end,
    image: p.cover_url ?? p.image_url ?? "",
    project_website_url: p.project_website_url,
  }));
}
